import {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useBackground} from '~/common/FabricInvitation/EventCreateDesign/components/Background/actions/useBackground';
import {selectIsPremium} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {FabricContext} from '~/common/fabric/context/FabricContext';
import {
  selectBackgroundColor,
  selectUsedPremiumFeature,
} from '~/common/fabric/reducers/fabricDesign/selectors';
import {selectUsedAs} from '~/OneStepCreateInvitation/reducers/fabricTemplate/selector';
import {EventCreateContext} from '../EventCreateContext';
import {useDowngradeModal} from './useDowngradeModal';
import {useExitPreviewModal} from './useExitPreviewModal';
import {useUpsellModal} from './useUpsellModal';

export const useModals = () => {
  const {canvas} = useContext(EventCreateContext);
  const {setShowBackground} = useContext(FabricContext);
  const {selectedBackground, onTextureChange, onColorChange} = useBackground(false);

  const [animatedBgReady, setAnimatedBgReady] = useState(false);

  const isPremium = useSelector(selectIsPremium);
  const usedPremiumFeature = useSelector(selectUsedPremiumFeature);
  const backgroundColor = useSelector(selectBackgroundColor);
  const isLoading = !useSelector(selectUsedAs);

  const {showExitPreviewModal} = useExitPreviewModal();
  const {onConfirmUpsell, isUsingPremiumFeature, renderUpsellModal} =
    useUpsellModal(showExitPreviewModal);
  const {toggleDowngradeModal, renderDowngradeModal} = useDowngradeModal();
  const {renderExitPremiumPreviewModal} = useExitPreviewModal();

  useEffect(() => {
    setShowBackground(
      (isPremium || usedPremiumFeature) &&
        ((selectedBackground && !selectedBackground?.isVideo) ||
          (selectedBackground?.isVideo && animatedBgReady))
    );
  }, [isPremium, usedPremiumFeature, selectedBackground, animatedBgReady, setShowBackground]);

  const resumeEditing = useCallback(() => {
    const textbox = canvas?.getObjects().find((obj) => obj.type === 'textbox');
    if (textbox) {
      canvas?.setActiveObject(textbox);
      canvas?.renderAll();
    }
  }, [canvas]);

  const onGoPremiumClick = useCallback(() => {
    onConfirmUpsell();
    if (selectedBackground) {
      onTextureChange(selectedBackground?.icon);
    } else {
      onColorChange(backgroundColor);
    }
    resumeEditing();
  }, [
    onConfirmUpsell,
    selectedBackground,
    resumeEditing,
    onTextureChange,
    onColorChange,
    backgroundColor,
  ]);

  const modalsComponents = (
    <>
      {renderUpsellModal}
      {renderExitPremiumPreviewModal}
      {renderDowngradeModal}
    </>
  );

  return {
    showBackground: selectedBackground !== undefined,
    selectedBackground,
    setAnimatedBgReady,
    isUsingPremiumFeature,
    toggleDowngradeModal,
    onGoPremiumClick,
    isLoading,
    modalsComponents,
  };
};
